import React from 'react';
import {useLocation, useNavigate} from 'react-router';
import {isMacLike} from '../../lib/platform';

/** The station search page's place field (PlaceAutocomplete `id`). */
export const STATION_SEARCH_INPUT_ID = 'station-search-input';

const SEARCH_PATH = '/app/stations/search';

/**
 * The K key: by letter on Latin layouts (AZERTY, Dvorak put it elsewhere),
 * by position where the layout types no Latin letter (Ctrl+л on Russian).
 */
const isK = (event: KeyboardEvent) =>
  /^[a-z]$/i.test(event.key)
    ? event.key.toLowerCase() === 'k'
    : event.code === 'KeyK';

/** What to show and announce for the shortcut, per platform. */
export const searchShortcut = () =>
  isMacLike()
    ? {label: '⌘K', aria: 'Meta+K'}
    : {label: 'Ctrl K', aria: 'Control+K'};

/**
 * Ctrl+K (⌘K on a Mac) opens station search, or focuses its field when it
 * is already open. A modifier shortcut, so WCAG 2.1.4 does not apply; only
 * the platform's own modifier, so macOS Ctrl+K (kill-line) still works in
 * text fields. Not inside an open dialog or menu (it would navigate under
 * it). The Help Centre keeps its own ⌘/Ctrl+K for article search; both
 * listen on window in no fixed order, so /help is skipped by path here.
 */
export const useSearchShortcut = () => {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const mac = isMacLike();
      if (
        !isK(event) ||
        event.metaKey !== mac ||
        event.ctrlKey === mac ||
        event.altKey ||
        event.shiftKey ||
        event.repeat ||
        event.isComposing ||
        event.defaultPrevented ||
        // Not isContentEditable: jsdom does not implement it.
        (event.target instanceof Element &&
          event.target.closest(
            '[contenteditable]:not([contenteditable="false"]), [aria-modal="true"], [role="menu"]',
          )) ||
        pathname.startsWith('/help')
      ) {
        return;
      }
      event.preventDefault();
      const input = document.getElementById(STATION_SEARCH_INPUT_ID);
      if (pathname.startsWith(SEARCH_PATH) && input) {
        input.focus();
      } else {
        navigate(SEARCH_PATH);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, pathname]);
};
